import { Link } from 'react-router-dom'
import type { Vod } from '../types'
import { formatDuration, formatRelative, formatViews } from '../lib/format'

function VodCard({ vod, now }: { vod: Vod; now: number }) {
  const title = `${vod.title} — ${new Date(vod.startTime).toLocaleString()}`
  const content = (
    <>
      <div className="vod-thumb">
        {vod.thumbnail ? (
          <img src={vod.thumbnail} alt="" loading="lazy" />
        ) : (
          <div style={{ width: '100%', height: '100%', background: 'var(--bg)' }} />
        )}
        <div className="vod-badges">
          {vod.subscriberOnly && <span className="badge badge-subscriber">Subscriber only</span>}
          <span className="badge">{formatViews(vod.views)} views</span>
          <span className="badge">{formatDuration(vod.durationMs)}</span>
        </div>
      </div>
      <div className="vod-body">
        <div className="vod-title">{vod.title}</div>
        <div className="vod-date">{formatRelative(vod.startTime, now)}</div>
      </div>
    </>
  )

  if (vod.subscriberOnly) {
    return (
      <div className="vod-card is-subscriber-only" title={`${title} — Subscriber only`}>
        {content}
      </div>
    )
  }

  return (
    <Link
      className="vod-card"
      to={`/v/${encodeURIComponent(vod.channelSlug)}/${vod.id}`}
      title={title}
    >
      {content}
    </Link>
  )
}

function SkeletonCard() {
  return (
    <div className="skeleton">
      <div className="skeleton-thumb" />
      <div className="skeleton-line" />
      <div className="skeleton-line short" />
    </div>
  )
}

type Props = {
  vods: Vod[] | null
  loading: boolean
  error: string | null
  onRetry: () => void
}

export function VodGrid({ vods, loading, error, onRetry }: Props) {
  // `now` is sampled once per render pass so relative times stay consistent
  // across the whole grid instead of ticking row by row.
  const now = Date.now()

  if (loading && (!vods || vods.length === 0)) {
    return (
      <div className="vod-grid">
        {Array.from({ length: 9 }, (_, i) => (
          <SkeletonCard key={i} />
        ))}
      </div>
    )
  }

  if (error && (!vods || vods.length === 0)) {
    return (
      <div className="state-box is-error">
        <h2>Could not load VODs</h2>
        <p>{error}</p>
        <button type="button" className="btn" onClick={onRetry}>
          Try again
        </button>
      </div>
    )
  }

  if (vods && vods.length === 0) {
    return (
      <div className="state-box">
        <h2>No VODs</h2>
        <p>This channel has no VODs in the last 30 days.</p>
      </div>
    )
  }

  return (
    <div className="vod-grid">
      {vods?.map((vod) => <VodCard key={vod.id} vod={vod} now={now} />)}
    </div>
  )
}